/*
  Editorial video — scroll-driven autoplay + click-to-toggle, grouped by ROW.

  - The ROW (editorial-wrapper / "Media Block") whose center is nearest the viewport middle
    is active: ALL its videos play (a side-by-side pair plays together). Other rows pause.
    So it's "one row at a time," not "one video" — otherwise a paired video gets force-paused.
  - Click anywhere on a video toggles it. Clicking play pauses OTHER rows but leaves row-mates
    alone. A manual pause "sticks" (userPaused) until that video scrolls out of view and back.
  - State classes on .editorial-media--video (.is-playing / .is-paused) drive the CSS.
    On tap we also flash .show-controls (button scales in, then out 1.5s later) — this replaces
    the :hover reveal on touch, where :hover would stick. Desktop hover still works (CSS gate).
  - prefers-reduced-motion: no autoplay (video stays paused; user can click play).

  Videos are muted (autoplay policy) + loop + no native controls (see _editorial-media.liquid).
*/
(() => {
  const medias = [...document.querySelectorAll('.editorial-media--video')];
  if (!medias.length) return;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const items = medias
    .map((el) => ({
      el,
      row: el.closest('.editorial-wrapper') || el,
      video: el.querySelector('video'),
      userPaused: false,
    }))
    .filter((it) => it.video);

  // Grouping: side-by-side (desktop, or mobile with Stack OFF) groups by ROW → the pair plays
  // together. When a row is stacked into a column on mobile, group by the individual video →
  // one at a time (stacked videos shouldn't both play).
  const mqStack = window.matchMedia('(max-width: 48rem)');
  const groupOf = (it) =>
    mqStack.matches && it.row.classList.contains('editorial-wrapper--stack') ? it.el : it.row;

  const setState = (it, playing) => {
    it.el.classList.toggle('is-playing', playing);
    it.el.classList.toggle('is-paused', !playing);
    const btn = it.el.querySelector('.em-video__toggle');
    if (btn) btn.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
  };

  const start = (it) => {
    if (!it.video.paused) return;
    const p = it.video.play();
    if (p && p.then) p.then(() => setState(it, true)).catch(() => setState(it, false));
    else setState(it, true);
  };

  const stop = (it, byUser) => {
    if (!it.video.paused) it.video.pause();
    setState(it, false);
    if (byUser) it.userPaused = true;
  };

  // Tap affordance: flash the play/pause button in, then scale it out after HINT_MS. Replaces the
  // :hover reveal on touch (CSS gates :hover to hover:hover devices); desktop hover still works.
  const HINT_MS = 1500;
  const hint = (it) => {
    it.el.classList.add('show-controls');
    clearTimeout(it.hintTimer);
    it.hintTimer = setTimeout(() => it.el.classList.remove('show-controls'), HINT_MS);
  };

  // Tap toggles the tapped video (play pauses OTHER rows, keeps row-mates) and flashes the button.
  for (const it of items) {
    const btn = it.el.querySelector('.em-video__toggle') || it.el;
    btn.addEventListener('click', () => {
      if (it.video.paused) {
        for (const o of items) { if (groupOf(o) !== groupOf(it)) stop(o, false); }
        it.userPaused = false;
        start(it);
      } else {
        stop(it, true);
      }
      hint(it);
    });
  }

  // The row nearest the viewport center is active; all its videos play, other rows pause.
  const updateAuto = () => {
    if (reduce) return;
    const vh = window.innerHeight;
    const mid = vh / 2;
    let best = null;
    let bestDist = Infinity;
    for (const it of items) {
      const r = it.el.getBoundingClientRect();
      const inView = r.bottom > 0 && r.top < vh;
      if (!inView) { it.userPaused = false; continue; }
      const dist = Math.abs(r.top + r.height / 2 - mid);
      if (dist < bestDist) { bestDist = dist; best = it; }
    }
    const activeGroup = best ? groupOf(best) : null;
    for (const it of items) {
      const active = activeGroup && groupOf(it) === activeGroup && !it.userPaused;
      if (active) start(it);
      else stop(it, false);
    }
  };

  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => { updateAuto(); ticking = false; });
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  items.forEach((it) => setState(it, false)); // start paused
  updateAuto();
})();
